import {
  API_CONFIG,
  getApiUrl,
  RAID_MYTHIC_PROGRESS_ENDPOINT,
} from '../config/apiConfig';

export type RaidProgressApiPayload = Record<string, unknown>;

export interface RaidMythicBannerState {
  name: string;
  difficulty: number;
  progress: number;
  totalBosses: number;
  guildName: string;
  lastUpdated: string;
  currentBoss: { id: number; name: string };
  isComplete: boolean;
  bossImageUrl?: string;
}

export type FetchRaidMythicBannerResult =
  | { ok: true; data: RaidMythicBannerState }
  | { ok: false; error: string };

function unwrapRaidProgressPayload(raw: RaidProgressApiPayload): RaidProgressApiPayload {
  const data = raw.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as RaidProgressApiPayload;
  }
  return raw;
}

function raidApiNumber(
  raw: RaidProgressApiPayload,
  camel: string,
  snake: string,
  fallback: number
): number {
  const v = raw[camel] ?? raw[snake];
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
    return Number(v);
  }
  return fallback;
}

function raidApiString(
  raw: RaidProgressApiPayload,
  camel: string,
  snake: string,
  fallback: string
): string {
  const v = raw[camel] ?? raw[snake];
  if (typeof v === 'string' && v.length) return v;
  if (v != null && v !== '') return String(v);
  return fallback;
}

function raidApiBoss(
  raw: RaidProgressApiPayload
): { id: number; name: string } | undefined {
  // Preferred going forward: API returns next boss under `next_boss.encounter_name`
  const nb = raw.nextBoss ?? raw.next_boss;
  if (nb && typeof nb === 'object' && !Array.isArray(nb)) {
    const o = nb as Record<string, unknown>;
    const name =
      typeof o.encounter_name === 'string' ? o.encounter_name : undefined;
    if (name && name.trim()) return { id: 0, name };
  }

  // Back-compat: some payloads provide `currentBoss`/`current_boss` with `id` + `name`
  const b = raw.currentBoss ?? raw.current_boss;
  if (!b || typeof b !== 'object' || Array.isArray(b)) return undefined;
  const o = b as Record<string, unknown>;
  const id = typeof o.id === 'number' ? o.id : Number(o.id);
  const name = typeof o.name === 'string' ? o.name : undefined;
  if (Number.isNaN(id)) return undefined;
  return { id, name: name || 'Boss' };
}

function raidApiBoolean(
  raw: RaidProgressApiPayload,
  camel: string,
  snake: string,
  fallback: boolean
): boolean {
  const v = raw[camel] ?? raw[snake];
  if (typeof v === 'boolean') return v;
  if (v === 1 || v === '1' || v === 'true') return true;
  if (v === 0 || v === '0' || v === 'false') return false;
  return fallback;
}

function raidApiOptionalString(
  raw: RaidProgressApiPayload,
  camel: string,
  snake: string
): string | undefined {
  const v = raw[camel] ?? raw[snake];
  if (typeof v === 'string' && v.length) return v;
  return undefined;
}

const VOIDSPIRE_JOURNAL_INSTANCE_ID = 1307;

const VOIDSPIRE_MYTHIC_BOSS_NAMES = [
  'Imperator Averzian',
  'Vorasius',
  'Fallen-King Salhadaar',
  'Vaelgor & Ezzorak',
  'Lightblinded Vanguard',
  'Crown of the Cosmos',
] as const;

type WingBoss = Record<string, unknown>;
type WingRow = Record<string, unknown>;

function findVoidspireWing(result: RaidProgressApiPayload): WingRow | null {
  const wings = result.wings;
  if (!Array.isArray(wings)) return null;
  for (const w of wings) {
    if (!w || typeof w !== 'object' || Array.isArray(w)) continue;
    const row = w as WingRow;
    const jid = row.journal_instance_id;
    const name =
      typeof row.instance_name === 'string' ? row.instance_name : '';
    if (jid === VOIDSPIRE_JOURNAL_INSTANCE_ID || /voidspire/i.test(name)) {
      return row;
    }
  }
  return null;
}

/** Parsed mythic wing / flat payload before merging with `guildName`, `difficulty`, etc. */
type ParsedMythicCore = {
  progress: number;
  totalBosses: number;
  lastUpdated: string;
  currentBoss: { id: number; name: string };
  isComplete: boolean;
  bossImageUrl?: string;
  raidName: string;
};

function parseRaidMythicProgressPayload(
  result: RaidProgressApiPayload
): ParsedMythicCore | null {
  const fetched = result.fetched_at ?? result.fetchedAt;
  const lastUpdated =
    typeof fetched === 'string' || typeof fetched === 'number'
      ? new Date(fetched as string | number).toLocaleString()
      : new Date().toLocaleString();

  const kb = result.mythic_bosses_killed;
  const kt = result.mythic_bosses_total;
  const hasAggregateMythic =
    typeof kb === 'number' && !Number.isNaN(kb) &&
    typeof kt === 'number' && !Number.isNaN(kt);

  const vs = findVoidspireWing(result);
  if (vs) {
    const progress =
      typeof vs.mythic_completed === 'number' && !Number.isNaN(vs.mythic_completed)
        ? vs.mythic_completed
        : 0;
    const totalBosses =
      typeof vs.mythic_total === 'number' && !Number.isNaN(vs.mythic_total)
        ? vs.mythic_total
        : 6;
    const rawName =
      typeof vs.instance_name === 'string' ? vs.instance_name : 'Voidspire';
    const raidName = rawName.replace(/^The\s+/i, '').trim() || 'Voidspire';

    const bosses = Array.isArray(vs.bosses)
      ? (vs.bosses as WingBoss[])
      : [];
    const nextFromApi = bosses.find(
      (b) => Number(b?.completed_count) === 0 && typeof b?.name === 'string'
    );

    let currentBoss: { id: number; name: string };
    const nextBossFromEndpoint = raidApiBoss(result);
    if (nextBossFromEndpoint?.name) {
      currentBoss = nextBossFromEndpoint;
    } else if (nextFromApi && typeof nextFromApi.name === 'string') {
      const nid = Number(nextFromApi.id);
      currentBoss = {
        id: Number.isFinite(nid) ? nid : 0,
        name: nextFromApi.name,
      };
    } else if (progress >= totalBosses && totalBosses > 0) {
      const last =
        VOIDSPIRE_MYTHIC_BOSS_NAMES[
          Math.min(totalBosses, VOIDSPIRE_MYTHIC_BOSS_NAMES.length) - 1
        ];
      currentBoss = { id: 0, name: last || 'Cleared' };
    } else {
      const nextName =
        VOIDSPIRE_MYTHIC_BOSS_NAMES[
          Math.min(progress, VOIDSPIRE_MYTHIC_BOSS_NAMES.length - 1)
        ] ?? 'Boss';
      currentBoss = { id: 0, name: nextName };
    }

    const isComplete = progress >= totalBosses;
    const bossImageUrl = raidApiOptionalString(
      result,
      'bossImageUrl',
      'boss_image_url'
    );

    const voidspireParsed: ParsedMythicCore = {
      progress,
      totalBosses,
      lastUpdated,
      currentBoss,
      isComplete,
      bossImageUrl,
      raidName,
    };

    // When API provides current-tier aggregate totals across multiple raids, prefer that over single-wing totals.
    if (hasAggregateMythic && kt > totalBosses) {
      return {
        progress: kb,
        totalBosses: kt,
        lastUpdated,
        // Keep the next actionable boss from the current active wing for banner context.
        currentBoss,
        isComplete: kb >= kt,
        bossImageUrl,
        raidName: 'Current Tier',
      };
    }

    return voidspireParsed;
  }

  if (hasAggregateMythic) {
    return {
      progress: kb,
      totalBosses: kt,
      lastUpdated,
      currentBoss: { id: 0, name: 'In progress' },
      isComplete: kb >= kt,
      bossImageUrl: raidApiOptionalString(
        result,
        'bossImageUrl',
        'boss_image_url'
      ),
      raidName: 'Mythic tier',
    };
  }

  return null;
}

/**
 * Fetches `raid-mythic-progress` and maps it to the Progress page banner model.
 * Shared by Progress and Home (mythic stat counter).
 */
export async function fetchRaidMythicBannerState(): Promise<FetchRaidMythicBannerResult> {
  try {
    const response = await fetch(getApiUrl(RAID_MYTHIC_PROGRESS_ENDPOINT), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_CONFIG.API_KEY,
      },
    });

    if (!response.ok) {
      return {
        ok: false,
        error: `HTTP error! status: ${response.status}`,
      };
    }

    const json = (await response.json()) as RaidProgressApiPayload;
    const result = unwrapRaidProgressPayload(json);

    if (result.success === false) {
      const msg =
        typeof result.message === 'string'
          ? result.message
          : typeof result.error === 'string'
            ? result.error
            : 'Raid progress unavailable';
      return { ok: false, error: msg };
    }

    const successFlag = result.success === true || result.success === 'true';
    const hasProgressFields =
      result.mythicProgress != null ||
      result.mythic_progress != null ||
      result.mythicTotal != null ||
      result.mythic_total != null ||
      result.mythic_bosses_killed != null ||
      result.mythic_bosses_total != null ||
      Array.isArray(result.wings);

    const mythicApiParsed = parseRaidMythicProgressPayload(result);

    if (mythicApiParsed) {
      const data: RaidMythicBannerState = {
        name: mythicApiParsed.raidName,
        difficulty: raidApiNumber(result, 'difficulty', 'difficulty_id', 5),
        progress: mythicApiParsed.progress,
        totalBosses: mythicApiParsed.totalBosses,
        guildName: raidApiString(result, 'guildName', 'guild_name', 'JUST VIBES'),
        lastUpdated: mythicApiParsed.lastUpdated,
        currentBoss: mythicApiParsed.currentBoss,
        isComplete: mythicApiParsed.isComplete,
        bossImageUrl: mythicApiParsed.bossImageUrl,
      };
      return { ok: true, data };
    }

    if (successFlag || hasProgressFields) {
      const progress = raidApiNumber(result, 'mythicProgress', 'mythic_progress', 0);
      const totalBosses = raidApiNumber(result, 'mythicTotal', 'mythic_total', 6);
      const lastRaw =
        result.lastUpdated ?? result.last_updated ?? result.updated_at;
      const lastUpdated =
        typeof lastRaw === 'string' || typeof lastRaw === 'number'
          ? new Date(lastRaw).toLocaleString()
          : new Date().toLocaleString();

      const data: RaidMythicBannerState = {
        name: raidApiString(result, 'currentRaid', 'current_raid', 'Voidspire'),
        difficulty: raidApiNumber(result, 'difficulty', 'difficulty_id', 5),
        progress,
        totalBosses,
        guildName: raidApiString(result, 'guildName', 'guild_name', 'JUST VIBES'),
        lastUpdated,
        currentBoss:
          raidApiBoss(result) ?? {
            id: 3176,
            name: 'Imperator Averzian',
          },
        isComplete: raidApiBoolean(result, 'isComplete', 'is_complete', false),
        bossImageUrl: raidApiOptionalString(result, 'bossImageUrl', 'boss_image_url'),
      };
      return { ok: true, data };
    }

    return {
      ok: false,
      error: 'Raid progress API returned an unexpected response',
    };
  } catch {
    return { ok: false, error: 'Unable to load live progression data' };
  }
}
